// Lado da página da fila de fotos. A foto é preparada (comprimida) aqui e guardada no
// aparelho; o envio é feito pelo service worker, que continua mesmo com o app fechado.
// Sem service worker (ex.: http na rede local), a própria página envia.
import { useSyncExternalStore } from 'react';
import { api } from './api';
import { preparePhoto } from './image';
import {
  allItems, deleteItem, notify, onQueueEvent, patchItem, persistent, processQueue, putItem, retryAll,
  type QueueItem, type QueueStatus, type UploadMeta,
} from './uploadQueue';

export type { UploadMeta } from './uploadQueue';

export interface UploadItem extends UploadMeta {
  localId: string;
  tid: string;
  visitId: string;
  status: QueueStatus;
  error?: string;
  previewUrl: string | null;
  capturedAt: number;
  photoId?: string;
  attempts: number;
  nextAt: number;
}

type Hooks = { onPresigned?: (createdNc: boolean) => void; onDone?: () => void };

/**
 * ID local da foto. `crypto.randomUUID` só existe em contexto seguro (https/localhost);
 * no celular abrindo http://IP-da-rede ele não existe e quebrava a captura.
 */
let seq = 0;
const newLocalId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `l${Date.now().toString(36)}${(++seq).toString(36)}${Math.random().toString(36).slice(2, 10)}`;

let snapshot: UploadItem[] = [];
const listeners = new Set<() => void>();
const hooks = new Map<string, Hooks>();
const previews = new Map<string, string>();
let swReady = false;

function toView(it: QueueItem): UploadItem {
  let url = previews.get(it.id) ?? null;
  if (!url && it.prepared) {
    try { url = URL.createObjectURL(it.blob); previews.set(it.id, url); } catch { url = null; }
  }
  return {
    localId: it.id, tid: it.tid, visitId: it.visitId, status: it.status, error: it.error, previewUrl: url, capturedAt: it.capturedAt,
    photoId: it.photoId, attempts: it.attempts, nextAt: it.nextAt, setor: it.setor, legenda: it.legenda, isNc: it.isNc, ncId: it.ncId, gravidade: it.gravidade,
  };
}

let reloading: Promise<void> | null = null;
let again = false;
async function reload() {
  if (reloading) { again = true; return reloading; }
  reloading = (async () => {
    do {
      again = false;
      const items = await allItems().catch(() => [] as QueueItem[]);
      const ids = new Set(items.map((i) => i.id));
      for (const [id, url] of previews) if (!ids.has(id)) { URL.revokeObjectURL(url); previews.delete(id); }
      snapshot = items.map(toView);
      listeners.forEach((l) => l());
    } while (again);
    reloading = null;
  })();
  return reloading;
}

onQueueEvent((e) => {
  if (e.type === 'changed') void reload();
  if (e.type === 'presigned') hooks.get(e.visitId)?.onPresigned?.(e.createdNc);
  if (e.type === 'done') hooks.get(e.visitId)?.onDone?.();
});

/** Pede ao service worker para enviar; sem ele, a página envia. */
async function kick() {
  if (swReady && navigator.serviceWorker?.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'mesa-upload-kick' });
    try {
      const reg = await navigator.serviceWorker.ready;
      // Background Sync (Chrome/Android): o navegador tenta de novo quando a internet voltar, mesmo com o app fechado.
      await (reg as ServiceWorkerRegistration & { sync?: { register(tag: string): Promise<void> } }).sync?.register('mesa-uploads');
    } catch { /* sem Background Sync */ }
    return;
  }
  void processQueue();
}

/** Fotos cuja preparação foi interrompida (app fechado no meio): prepara de novo. */
async function resumePreparing() {
  const items = await allItems();
  for (const it of items.filter((i) => !i.prepared && i.status === 'preparing')) void prepare(it.id, it.blob);
}

async function prepare(id: string, file: Blob) {
  try {
    const p = await preparePhoto(file as File);
    await patchItem(id, { blob: p.blob, prepared: true, contentType: p.contentType, width: p.width, height: p.height, capturedAt: p.capturedAt, status: 'waiting', nextAt: 0 });
    void kick();
  } catch (e) {
    await patchItem(id, { status: 'failed', error: e instanceof Error ? e.message : 'Não foi possível ler a foto.' });
  }
}

export const uploads = {
  /** Guarda a foto no aparelho e começa a enviar na hora. */
  async add(tid: string, visitId: string, file: File, meta: UploadMeta) {
    const id = newLocalId();
    const now = Date.now();
    const item: QueueItem = {
      ...meta, id, tid, visitId, blob: file, prepared: false, contentType: file.type || 'image/jpeg', width: null, height: null,
      capturedAt: now, createdAt: now, status: 'preparing', attempts: 0, nextAt: 0, leaseUntil: 0,
    };
    await putItem(item);
    notify({ type: 'changed' });
    // Já avisa o envio: se o app fechar antes de preparar a foto, a original sobe sozinha.
    void kick();
    await prepare(id, file);
  },

  async retry(localId: string) {
    await retryAll([localId]);
    void kick();
  },

  async discard(localId: string) {
    const it = (await allItems()).find((x) => x.id === localId);
    if (!it) return;
    await deleteItem(localId);
    notify({ type: 'changed' });
    if (it.photoId) await api.del(`/api/t/${it.tid}/visits/${it.visitId}/photos/${it.photoId}`).catch(() => undefined);
    hooks.get(it.visitId)?.onDone?.();
  },

  /** Ao cancelar a visita, tira da fila tudo o que era dela. */
  async clearVisit(visitId: string) {
    for (const it of await allItems()) if (it.visitId === visitId) await deleteItem(it.id);
    notify({ type: 'changed' });
  },

  setHooks(visitId: string, h: Hooks) {
    hooks.set(visitId, h);
    return () => { if (hooks.get(visitId) === h) hooks.delete(visitId); };
  },

  kick,
  hasActive: () => snapshot.some((x) => x.status !== 'failed'),
  hasAny: () => snapshot.length > 0,
};

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

const all = () => snapshot;
export function useUploads(visitId?: string) {
  const items = useSyncExternalStore(subscribe, all);
  return visitId ? items.filter((x) => x.visitId === visitId) : items;
}

/** true quando as fotos ficam guardadas no aparelho até subir (IndexedDB disponível). */
let durable = true;
export const uploadsDurable = () => durable;

// Início: carrega a fila guardada, retoma o que ficou pela metade e mantém tentando.
void (async () => {
  durable = await persistent();
  await reload();
  await resumePreparing();
  if ('serviceWorker' in navigator && window.isSecureContext && import.meta.env.PROD) {
    navigator.serviceWorker.addEventListener('controllerchange', () => { swReady = true; void kick(); });
    // Só confia no service worker se ele já controla a página (no primeiro acesso, a página envia).
    const ready = await Promise.race([navigator.serviceWorker.ready.then(() => true), new Promise<boolean>((r) => setTimeout(() => r(false), 4000))]).catch(() => false);
    swReady = ready && !!navigator.serviceWorker.controller;
  }
  if (snapshot.length) void kick();
})();

window.addEventListener('online', () => { void retryAll().then(kick); });
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible' && snapshot.length) void kick(); });
// Rede de segurança: enquanto houver fotos na fila, insiste a cada 20s.
window.setInterval(() => { if (snapshot.some((x) => x.status !== 'failed')) void kick(); }, 20_000);

// Sem armazenamento no aparelho a foto só existe na memória: avisa antes de sair.
window.addEventListener('beforeunload', (e) => {
  if (!durable && snapshot.length) {
    e.preventDefault();
    e.returnValue = '';
  }
});
